import { Attributes, List, Misc, ModelBase, Rules, Validation } from '@singularsystems/neo-core';
import TemporalEntity from '../TemporalEntity';
import Investment from '../Investment/Investment';
import Note from '../Notes/Note';
import Investor from '../Investors/Investor';
import NewInvestorInvestmentCommand from './Commands/NewInvestorInvestmentCommand';
import TransactionRequest from '../Transactions/TransactionRequest';
import InvestorInvestmentDetail from './InvestorInvestmentDetail';
import INoteListHost from '../Notes/INoteListHost';
import { IInvestmentCommonFields, IInvestmentHost } from '../Investment/IInvestmentHost';
import Entity from '../Catalogue/Investments/Entity';
import { isMandatory, MandatoryAndVisibleSetting } from '../Config/FieldConfig/FieldSetting.enum';
import { AppService, DomainTypes } from '../../DomainTypes';
import InvestorInvestmentFieldConfig from '../Config/FieldConfig/InvestorInvestmentFieldConfig';
import AuthorisationValues from '../ValueObjects/AuthorisationValues';

export default class InvestorInvestment extends TemporalEntity implements INoteListHost, IInvestmentHost {
    static typeName = "InvestorInvestment";

    constructor() {
        super();

        this.makeObservable();
    }

    public investorInvestmentId: number = 0;

    @Attributes.Serialisation(Misc.SerialiseType.NotNull)
    public investmentId: number | null = null;

    @Attributes.ChildObject(Investment)
    public investment: Investment = new Investment();

    @Rules.Required()
    public entityId: number | null = null;

    @Attributes.Serialisation(Misc.SerialiseType.NotNull)
    @Rules.Required()
    public investorId: number | null = null;

    public investor: Investor | null = null;

    @Attributes.ChildObject(InvestorInvestmentDetail, true)
    public investorInvestmentDetail: InvestorInvestmentDetail = new InvestorInvestmentDetail();

    @Attributes.Nullable()
    public issuerInvestmentId: number | null = null;

    @Attributes.Nullable()
    public issuerInvestmentIdLink: number | null = null;

    public transactionRequests = new List(TransactionRequest);

    @Rules.StringLength(20)
    public refNo: string = "";

    @Attributes.Nullable()
    @Attributes.Integer()
    public investorIdBeneficialOwner: number | null = null;

    public isApproved: boolean = false;

    @Attributes.NoTracking(Misc.SerialiseType.FullOnly)
    @Attributes.ChildObject(() => LookupFields)
    public lookup: LookupFields = new LookupFields();

    public notes = new List(Note);

    @Attributes.ChildObject(AuthorisationValues, true)
    public authorisation: AuthorisationValues = new AuthorisationValues();

    public isPendingDelete: boolean = false;

    // Client only properties / methods

    public get issuerInvestmentName() {
        return `(${this.issuerInvestmentId ?? this.issuerInvestmentIdLink}) ${this.lookup.issuerInvestmentName}`;
    }

    /** Indicates that the investment child object is shared with an issuer investment. */
    public get isLinkedInvestment() {
        return this.investmentId !== null && this.investmentId > 0 && this.investmentId === this.lookup.issuerInvestmentLinkInvestmentId;
    }

    public get commonFields(): IInvestmentCommonFields {
        return {
            lastAllocationDate: this.lookup.issuerInvestmentAllocationDate,
            productTypeId: this.investorInvestmentDetail.productTypeId,
            isCeded: this.investorInvestmentDetail.isCeded,
        }
    }

    public setIdOnNote(note: Note): void {
        note.investorInvestmentId = this.investorInvestmentId;
    }

    public static fromCreateCommand(command: NewInvestorInvestmentCommand) {
        const investorInvestment = new InvestorInvestment();
        investorInvestment.investorId = command.investorId;
        investorInvestment.entityId = command.entityId;
        investorInvestment.issuerInvestmentId = command.issuerInvestmentId;
        investorInvestment.lookup = command.lookup;
        investorInvestment.investorInvestmentDetail.productTypeId = command.productTypeId;

        return investorInvestment;
    }

    protected addBusinessRules(rules: Validation.Rules<this>) {
        super.addBusinessRules(rules);

        // Rules affected by configurable mandatory settings
        rules.failWhen(c => !c.investment.initialEndDate && this.isRequired("initialEndDate"), "Initial end date is required.");
        rules.failWhen(c => !c.investment.compoundingCycleId && this.isRequired("compoundingCycleId"), "Compounding cycle is required.");
    }

    public toString(): string {
        if (this.isNew) {
            return "New investor investment";
        } else {
            return "Investor Investment";
        }
    }

    private isRequired(fieldName: keyof InvestorInvestmentFieldConfig) {
        return isMandatory(AppService.get(DomainTypes.Services.DataCache).fieldConfig.investorInvestmentFields[fieldName] as MandatoryAndVisibleSetting);
    }
}

export class LookupFields extends ModelBase {
    static typeName = "LookupFields";

    constructor() {
        super();
        this.makeObservable();
    }

    public investorName: string | null = null;

    public issuerInvestmentName: string | null = null;

    public investmentName: string | null = null;

    @Attributes.Date(Misc.TimeZoneFormat.None)
    public issuerInvestmentAllocationDate: Date | null = null;

    public issuerInvestmentLinkInvestmentId: number | null = null;

    public hasVestingTrust: boolean = false;

    public isLiquidityProvider: boolean = false;

    public isExempt: boolean = false;

    public isInvestorAuthorised: boolean = false;

    public isInvestorRestricted: boolean = false;

    @Attributes.Nullable()
    public approvalId: number | null = null;

    // Client only properties / methods

    @Attributes.NoTracking()
    public entity?: Entity;

    public setEntity(entity?: Entity) {
        this.entity = entity;
        if (entity) {
            this.hasVestingTrust = entity.hasVestingTrust;
        }
    }
}